import { useEffect, useState } from "react";
import { Check, X, BadgePercent, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { api, formatTL, formatDate, formatApiError, SUB_STATUS, PAYMENT_STATUS, PACKAGE_STYLE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const Limit = ({ v }) => (v == null ? "Sınırsız" : v);

export default function SubscriptionPage() {
  const { user, subscription: sub, refresh } = useAuth();
  const [packages, setPackages] = useState([]);
  const [features, setFeatures] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [history, setHistory] = useState({ payments: [], changes: [] });
  const [requests, setRequests] = useState([]);
  const [target, setTarget] = useState(null);
  const [code, setCode] = useState("");
  const isBroker = user?.role === "BROKER";

  const load = async () => {
    const [{ data: p }, { data: c }, { data: h }, { data: r }] = await Promise.all([
      api.get("/packages"), api.get("/campaigns"), api.get("/subscription/history"), api.get("/subscription/change-requests"),
    ]);
    setPackages(p.packages);
    setFeatures(p.features);
    setCampaigns(c);
    setHistory(h);
    setRequests(r);
  };
  useEffect(() => { load(); }, []);

  if (!sub) return null;
  const current = packages.find((p) => p.code === sub.package_code);
  const st = SUB_STATUS[sub.status];
  const pending = requests.find((r) => r.status === "PENDING");
  const lastDecided = requests.find((r) => r.status !== "PENDING");
  const pkgName = (code) => packages.find((p) => p.code === code)?.name || code;

  const confirmChange = async () => {
    try {
      await api.post("/subscription/change", { package_code: target.code, campaign_code: code || null });
      toast.success("Paket değişikliği talebiniz Süper Admin onayına gönderildi");
      setTarget(null);
      setCode("");
      await refresh();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const offersFor = (pkgCode) => campaigns.filter((c) => c.to_package === pkgCode && (!c.from_package || c.from_package === sub.package_code));

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Abonelik</div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Aboneliğim</h1>
      </div>

      {pending && (
        <div data-testid="pending-package-request" className="rounded-xl border border-blue-200 bg-blue-50 px-5 py-3 text-sm text-blue-900">
          <b>{pkgName(pending.from_package)} → {pkgName(pending.to_package)}</b> geçiş talebiniz {new Date(pending.created_at).toLocaleString("tr-TR")} tarihinde gönderildi ve Süper Admin onayı bekliyor. Onaylandığında yeni paket özellikleri otomatik olarak açılır.
        </div>
      )}
      {!pending && lastDecided?.status === "REJECTED" && (
        <div data-testid="rejected-package-request" className="rounded-xl border border-red-200 bg-red-50 px-5 py-3 text-sm text-red-900">
          Son paket talebiniz ({pkgName(lastDecided.to_package)}) reddedildi{lastDecided.reject_reason ? `: ${lastDecided.reject_reason}` : "."}
        </div>
      )}

      <Card className="p-6 grid grid-cols-1 md:grid-cols-4 gap-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Paket</div>
          <Badge data-testid="current-package" variant="outline" className={`text-sm ${PACKAGE_STYLE[sub.package_code]}`}>{sub.package_name}</Badge>
          <div className="text-sm text-slate-600 mt-2">{formatTL(sub.price)} / 30 gün</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Durum</div>
          <Badge data-testid="subscription-status" variant="outline" className={st.cls}>{st.label}</Badge>
          <div className="text-sm text-slate-600 mt-2">Ödeme: {PAYMENT_STATUS[sub.payment_status]}</div>
        </div>
        <div className="md:col-span-2">
          <div className="flex justify-between text-xs text-slate-500 mb-2">
            <span>{formatDate(sub.period_start)}</span>
            <span data-testid="subscription-days-left" className="font-semibold text-slate-900">{sub.day_of_period}. gün — {sub.days_left} gün kaldı</span>
            <span>{formatDate(sub.period_end)}</span>
          </div>
          <Progress value={(sub.day_of_period / 30) * 100} className="h-2" />
          {sub.discount && (
            <div className="mt-3 text-sm text-emerald-700 flex items-center gap-1.5">
              <BadgePercent className="w-4 h-4" /> Sonraki ödemede %{sub.discount.percent} indirim ({sub.discount.name})
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {packages.map((p) => {
          const isCurrent = p.code === sub.package_code;
          const up = current && p.order > current.order;
          const offers = offersFor(p.code);
          return (
            <Card key={p.code} data-testid={`package-card-${p.code}`} className={`p-6 flex flex-col ${isCurrent ? "ring-2 ring-slate-900" : ""}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{p.name}</h3>
                {isCurrent && <Badge className="bg-slate-900">Mevcut</Badge>}
              </div>
              <div className="text-3xl font-extrabold mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{formatTL(p.price)}</div>
              <div className="text-xs text-slate-500 mb-4">30 günlük dönem</div>
              <ul className="space-y-2 text-sm flex-1">
                {Object.entries(features).map(([k, label]) => (
                  <li key={k} className={`flex items-center gap-2 ${p.features.includes(k) ? "text-slate-800" : "text-slate-400"}`}>
                    {p.features.includes(k) ? <Check className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4" />} {label}
                  </li>
                ))}
                <li className="text-slate-600 pt-2 border-t">Kullanıcı: <b><Limit v={p.max_users} /></b> · Portföy: <b><Limit v={p.max_portfolios} /></b></li>
              </ul>
              {offers.map((c) => (
                <div key={c.id} className="mt-3 text-xs rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2">
                  <b>{c.name}</b>: %{c.discount_percent} indirim · Kod: <span className="font-mono">{c.code}</span>
                </div>
              ))}
              {isBroker && !isCurrent && !pending && (
                <Button
                  data-testid={`change-package-${p.code}`}
                  onClick={() => { setTarget({ ...p, up }); setCode(offers[0]?.code || ""); }}
                  className={`mt-4 ${up ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                  variant={up ? "default" : "outline"}
                >
                  {up ? <><ArrowUpCircle className="w-4 h-4 mr-2" />Yükselt</> : <><ArrowDownCircle className="w-4 h-4 mr-2" />Düşür</>}
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="overflow-hidden">
        <div className="p-5 border-b font-semibold">Ödeme Geçmişi</div>
        {history.payments.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Kayıt yok.</div> :
          history.payments.map((p) => (
            <div key={p.id} className="px-5 py-3 border-b last:border-0 flex justify-between text-sm">
              <span>{formatDate(p.paid_at)} · {p.package_code} · {formatDate(p.period_start)} – {formatDate(p.period_end)}</span>
              <span className="font-semibold">{formatTL(p.amount)}</span>
            </div>
          ))}
      </Card>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{target?.up ? "Paketi Yükselt" : "Paketi Düşür"}: {target?.name}</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-4 text-sm">
              {target.up ? (
                <p className="text-slate-600">Talebiniz Süper Admin onayına gönderilir. Onaylandığında yeni paketteki özellikler otomatik açılır; mevcut müşteri ve portföy verileriniz korunur.</p>
              ) : (
                <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {target.name} paketinde olmayan özellikler kilitlenecek:{" "}
                  <b>{current?.features.filter((f) => !target.features.includes(f)).map((f) => features[f]).join(", ") || "yok"}</b>.
                  Verileriniz silinmez; tekrar yükselttiğinizde geri açılır.
                </p>
              )}
              <div>
                <div className="text-xs text-slate-500 mb-1">Kampanya kodu (isteğe bağlı)</div>
                <Input data-testid="campaign-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Örn: GOLD20" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setTarget(null)}>Vazgeç</Button>
                <Button data-testid="confirm-package-change" onClick={confirmChange} className="bg-slate-900">Onaya Gönder</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
